"use client";

import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { api } from "@/lib/useApi";
import { Button, ErrorBox } from "../ui";
import { groupRows, shownGroups, type Value } from "./SettingFields";
import { FIELD_META } from "./fields";
import { SectionHead, SettingsTable, type TableGroup } from "./SettingsTable";
import { useDialog } from "@/components/Dialog";

/** A setting as `GET /api/settings` returns it. */
interface Entry {
  value: Value;
  defaults: Value;
}

/** How long after the last change an edit is saved (typing a number does not save every digit). */
const SAVE_DELAY_MS = 600;

/** The last save: on its way, done, or its error. */
type SaveStatus = "saving" | "saved" | { error: string } | null;

/**
 * Saves a value by itself: SAVE_DELAY_MS after its last change, and right away when the form goes (the section left).
 */
function useAutoSave<T>(value: T, persist: (v: T) => Promise<unknown>): void {
  const text = JSON.stringify(value);
  const saved = useRef(text);
  const pending = useRef<string | null>(null);
  const persistRef = useRef(persist);
  useEffect(() => {
    persistRef.current = persist;
  });
  const flush = useCallback(async () => {
    const waiting = pending.current;
    if (waiting === null) return;
    pending.current = null;
    saved.current = waiting;
    await persistRef.current(JSON.parse(waiting) as T);
  }, []);
  useEffect(() => {
    if (text === saved.current) return;
    pending.current = text;
    const timer = setTimeout(() => void flush(), SAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text, flush]);
  useEffect(() => () => void flush(), [flush]);
}

/** The last save's outcome under a form: saving, saved, or the error. */
function SaveLine({ status }: { status: SaveStatus }) {
  const { t } = useI18n();
  if (status !== null && typeof status === "object") return <div className="mt-2"><ErrorBox>{status.error}</ErrorBox></div>;
  const text: Record<"saving" | "saved" | "idle", string> = { saving: t("set.saving"), saved: `✓ ${t("set.saved")}`, idle: t("set.autoSave") };
  return <span className={clsx("text-[11.5px]", status === "saved" ? "text-good-ink" : "text-muted")}>{text[status ?? "idle"]}</span>;
}

/**
 * Settings → Chart layers (display): one group per layer, each value with its default; saved as they change — back at
 * every default, the user's own entry goes (a later change of a default reaches them).
 */
export function LayerSettings({ layers, onSaved }: { layers: Entry; onSaved: () => void }) {
  const i18n = useI18n();
  const { t } = i18n;
  const dialog = useDialog();
  const [lv, setLv] = useState<Value>(layers.value);
  const [status, setStatus] = useState<SaveStatus>(null);
  const write = async (request: () => Promise<unknown>) => {
    setStatus("saving");
    try {
      await request();
      setStatus("saved");
      onSaved();
    } catch (e) {
      setStatus({ error: e instanceof Error ? e.message : String(e) });
    }
  };
  const defaults = JSON.stringify(layers.defaults);
  useAutoSave(lv, (v) => write(() => (JSON.stringify(v) === defaults ? api("/api/settings/layers", { method: "DELETE" }) : api("/api/settings/layers", { method: "PUT", json: v }))));
  const restore = async () => (await dialog.confirm(t("set.confirmReset", { name: t("set.section.layers.title") }), { danger: true })) && setLv(layers.defaults);
  const groups: TableGroup[] = shownGroups(lv, FIELD_META.layers).map((g) => ({
    key: `layers.${g}`,
    title: t(`set.group.layers.${g}`),
    rows: groupRows(i18n, g, { value: lv[g] as Value, defaults: layers.defaults[g] as Value, meta: FIELD_META.layers, onChange: (v) => setLv({ ...lv, [g]: v }) }),
  }));
  return (
    <section>
      <SectionHead title={t("set.section.layers.title")} description={t("set.layers.note")} />
      <SettingsTable groups={groups} />
      <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-line pt-2">
        <SaveLine status={status} />
        <Button size="xs" variant="ghost" className="ml-auto" disabled={status === "saving"} onClick={restore}>{t("set.restoreDefaults")}</Button>
      </div>
    </section>
  );
}
